import { PropsWithChildren, Suspense } from 'react';

import { isLocalHost, isSameOrigin, isSimulatedLiveHost } from '@xemplo/common-util';
import { XemploLoader } from '@xemplo/loader';
import { XemploAppSettingsProvider } from '@xemplo/xemplo-app-settings';

import { useMobileHandover, useSessionRecord } from '../hooks';

import { CrossAppDataHandoverProps } from './data-handover.types';

/**
 * Resolves the cross-app session record and seeds the app settings with it.
 *
 * The record is read *above* `XemploAppSettingsProvider` so the persona is
 * already correct on the provider's first render. Applying it below, from an
 * effect, left one committed render holding no persona (or the tab's previous
 * one) — long enough for `withSelectedPersona` to render the page and fire its
 * queries against the wrong persona. See ADR 0013.
 */
export function CrossAppDataHandover({
  settings,
  children,
}: PropsWithChildren<CrossAppDataHandoverProps>) {
  return (
    // A real fallback, not null: this gate now blocks the whole app on one
    // request, so a slow backend must read as loading rather than as a blank
    // page. `@xemplo/loader` is a type:ui lib and this one is type:feature, so
    // the layering that previously forced `null` no longer applies.
    <Suspense fallback={<XemploLoader />}>
      <SessionGate settings={settings}>{children}</SessionGate>
    </Suspense>
  );
}

function SessionGate({
  settings,
  children,
}: PropsWithChildren<CrossAppDataHandoverProps>) {
  const session = useSessionRecord(settings.VITE_DS_URL);

  // A sub-route means the app is served by the tenant's HR host, so the origin
  // beats the record — one row shared by every origin on the same DS. `baseUrl`
  // is a build-time constant though, so it is set in local dev too: there the
  // origin is a dev server with no HR API behind it, and the record still wins.
  const isTenantHost = !!settings.baseUrl && !isLocalHost() && !isSimulatedLiveHost();
  const shouldDiscardPersonaData = isTenantHost && !isSameOrigin(session?.instanceUrl);

  return (
    <XemploAppSettingsProvider
      {...settings}
      dsPersonaId={shouldDiscardPersonaData ? undefined : session?.personaId}
      dsCompanyId={shouldDiscardPersonaData ? undefined : session?.companyId}
      dsSessionId={session?.sessionId}
      // the guard shows a no-access screen instead of persona selection,
      // because the user does have a persona — on a different instance.
      stalePersonaId={shouldDiscardPersonaData ? session?.personaId : undefined}
      // `instanceUrl` is the session-state contract name for the HR instance URL.
      hrInstanceUrl={isTenantHost ? window.location.origin : session?.instanceUrl}
    >
      <MobileHandoverGate>{children}</MobileHandoverGate>
    </XemploAppSettingsProvider>
  );
}

/** Needs the settings setters, so it has to sit below the provider. */
function MobileHandoverGate({ children }: PropsWithChildren) {
  useMobileHandover();
  return children;
}
